import { getAccessToken } from '../authentication/supabaseService';

/** Whether a waste entry has been given a product identity yet (M72). */
export type WasteLinkStatus = 'unlinked' | 'linked' | 'skipped';

/** How the link was established: deterministic name hit, AI match, or a human. */
export type WasteLinkMethod = 'exact' | 'ai' | 'manual';

export interface WasteEntry {
  id: string;
  restaurantId: string;
  loggedBy: string | null;
  createdAt: Date;
  updatedAt: Date;
  wasteDate: Date;
  wasteType: 'dish' | 'ingredient';
  itemName: string;
  quantity: number;
  unit: 'g' | 'ml' | 'p';
  reason: 'expired' | 'over_prep' | 'mistake' | 'bad_quality' | 'accident' | 'other' | null;
  reasonNote: string | null;
  // --- product link (INGREDIENT entries only; all optional & additive, and the iOS
  // create path never sets them). Dish entries carry no stored link: their cost-price
  // analysis is derived from the name at read time — see utils/reports/wasteByProduct.ts.
  linkStatus: WasteLinkStatus;
  /** The bought product's stable identity. */
  linkedPlatform: string | null;
  linkedArticleId: string | null;
  linkedProductName: string | null;
  linkMethod: WasteLinkMethod | null;
  linkConfidence: number | null;
  linkedAt: Date | null;
  linkedBy: string | null;
}

export interface CreateWasteRequest {
  wasteDate?: string;
  wasteType: 'dish' | 'ingredient';
  itemName: string;
  quantity: number;
  unit: 'g' | 'ml' | 'p';
  reason?: string | null;
  reasonNote?: string | null;
}

export interface UpdateWasteRequest {
  wasteDate?: string;
  wasteType?: 'dish' | 'ingredient';
  itemName?: string;
  quantity?: number;
  unit?: 'g' | 'ml' | 'p';
  reason?: string | null;
  reasonNote?: string | null;
}

function parseWasteEntry(data: any): WasteEntry {
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
    wasteDate: new Date(data.wasteDate),
    // Default to 'unlinked' so the page still renders against a server that predates M72.
    linkStatus: data.linkStatus ?? 'unlinked',
    linkedPlatform: data.linkedPlatform ?? null,
    linkedArticleId: data.linkedArticleId ?? null,
    linkedProductName: data.linkedProductName ?? null,
    linkMethod: data.linkMethod ?? null,
    linkConfidence: typeof data.linkConfidence === 'number' ? data.linkConfidence : null,
    linkedAt: data.linkedAt ? new Date(data.linkedAt) : null,
    linkedBy: data.linkedBy ?? null,
  };
}

export async function fetchWaste(restaurantId: string): Promise<WasteEntry[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/waste/restaurant/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch waste entries: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map(parseWasteEntry);
}

export async function createWaste(restaurantId: string, request: CreateWasteRequest): Promise<WasteEntry> {
  const token = await getAccessToken();
  const res = await fetch(`/api/waste/restaurant/${restaurantId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw new Error(`Failed to create waste entry: ${res.status} ${res.statusText}`);
  }
  return parseWasteEntry(await res.json());
}

export async function updateWaste(id: string, request: UpdateWasteRequest): Promise<WasteEntry> {
  const token = await getAccessToken();
  const res = await fetch(`/api/waste/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw new Error(`Failed to update waste entry: ${res.status} ${res.statusText}`);
  }
  return parseWasteEntry(await res.json());
}

export async function deleteWaste(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/waste/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete waste entry: ${res.status} ${res.statusText}`);
  }
}

// --- product linking (M72) ------------------------------------------------

/** Match outcome for one entry in a dry-run: bind-ready, needs a human, or nothing found. */
export type WasteMatchStatus = 'auto' | 'review' | 'unmatched';

/** An order-history product an ingredient entry could be linked to. */
export interface WasteProductCandidate {
  platform: string;
  articleId?: string;
  productName: string;
  supplier?: string;
  unitPrice?: number;
}

/** The match the planner would apply. `method` is optional — when the server omits it,
 *  a confidence of 1 means the deterministic tier hit and anything less came from AI. */
export interface WasteIngredientPlanRow {
  wasteId: string;
  itemName: string;
  status: WasteMatchStatus;
  best?: WasteProductCandidate & { confidence: number; method?: WasteLinkMethod };
  suggestions: WasteProductCandidate[];
}

/** Method for a planned bind: what the server said, else derived from its confidence. */
export function planMethod(best: { confidence: number; method?: WasteLinkMethod }): WasteLinkMethod {
  return best.method ?? (best.confidence >= 1 ? 'exact' : 'ai');
}

export interface WasteLinkDryRunSummary {
  total: number;
  auto: number;
  review: number;
  unmatched: number;
}

/** Ingredient entries only — dish entries resolve by name and are never planned. */
export interface WasteLinkDryRunResult {
  ingredients: WasteIngredientPlanRow[];
  summary: WasteLinkDryRunSummary;
}

/** One reviewed decision sent to commit. `bind` carries the product identity.
 *  `kind` is required on EVERY resolution, skips included. */
export interface WasteLinkResolution {
  wasteId: string;
  kind: 'ingredient';
  action: 'bind' | 'skip';
  platform?: string;
  articleId?: string;
  productName?: string;
  confidence?: number;
  method: WasteLinkMethod;
}

export interface WasteLinkCommitSummary {
  linked: number;
  skipped: number;
  failed: number;
}

/**
 * Single-entry manual link (`PUT /api/waste/:id/link`). The body is a commit resolution
 * minus `wasteId`, so both link flows build the same shape. `kind` must be sent explicitly
 * — an empty body is rejected server-side so a stray request can't wipe a link. `kind: null`
 * unlinks; `action: 'skip'` parks the entry as `'skipped'` so the automation stops proposing
 * it. `articleId` is optional: no-articleId platforms key their identity off the name.
 *
 * INGREDIENT entries only. The route 400s on a dish row — a dish's analysis is derived from
 * its name, so there is nothing to store.
 */
export type WasteLinkUpdate =
  | { kind: 'ingredient'; platform: string; articleId?: string; productName: string; method?: WasteLinkMethod }
  | { kind: null }
  | { action: 'skip' };

/** Plan the links for every entry still `unlinked`. Read-only — writes nothing. */
export async function dryRunWasteLinks(restaurantId: string): Promise<WasteLinkDryRunResult> {
  const token = await getAccessToken();
  const res = await fetch(`/api/waste/restaurant/${restaurantId}/link/dry-run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    throw new Error(`Failed to analyze waste links: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

/** Apply the reviewed decisions in one transaction. */
export async function commitWasteLinks(
  restaurantId: string,
  resolutions: WasteLinkResolution[]
): Promise<WasteLinkCommitSummary> {
  const token = await getAccessToken();
  const res = await fetch(`/api/waste/restaurant/${restaurantId}/link/commit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ resolutions }),
  });
  if (!res.ok) {
    throw new Error(`Failed to commit waste links: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.summary ?? data;
}

/** Link, relink or unlink one entry by hand. */
export async function updateWasteLink(id: string, update: WasteLinkUpdate): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/waste/${id}/link`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(update),
  });
  if (!res.ok) {
    throw new Error(`Failed to update waste link: ${res.status} ${res.statusText}`);
  }
}

export async function fetchIngredientSuggestions(restaurantId: string): Promise<string[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/waste/restaurant/${restaurantId}/suggestions/ingredients`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch ingredient suggestions: ${res.status} ${res.statusText}`);
  }
  return res.json();
}
